/* eslint-disable jsx-a11y/no-static-element-interactions */
import { findParentNodeClosestToPos } from '@tiptap/core';
import classNames from 'classnames';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import {
  TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE,
  TIPTAP_COLLAPSIBLE_ITEM_TYPE,
  TIPTAP_COLLAPSIBLE_LIST_TYPE,
} from 'ricos-content';
import {
  EditorContext,
  EditorQueryContext,
  PluginsEventsContext,
  RicosContext,
} from 'ricos-context';
import type { PluginProps } from 'ricos-types';

import collapsibleListStyles from '../../statics/styles/collapsible-list-component.rtlignore.scss';
import collapsibleListItemStyles from '../../statics/styles/collapsible-list-pair.rtlignore.scss';
import ExpandCollapseButton from '../components/ExpandCollapseButton';
import NewPairButton from '../components/NewPairButton';
import {
  COLLAPSIBLE_LIST_ITEM,
  COLLAPSIBLE_LIST_ITEM_BODY,
  COLLAPSIBLE_LIST_ITEM_TITLE,
} from '../dataHooks';
import { DndIcon } from '../icons';
import { supportedPluginTypes } from './defaults';
import { isInCollapsibleList } from './utils';

const FloatingAddPluginMenu = React.lazy(() => import('./lazy-FloatingAddPluginMenu'));

const preventFocusJumpToFirstNode = event => {
  event.preventDefault();
  event.stopPropagation();
};

export const CollapsibleList: React.FC<PluginProps> = ({
  editor,
  node,
  NodeViewContent,
  componentData,
  getPos,
}) => {
  const { t, languageDir } = useContext(RicosContext);
  const editorQuery = useContext(EditorQueryContext);
  const pluginsEvents = useContext(PluginsEventsContext);

  const { isNodeSelected } = editorQuery.query;
  const styles = collapsibleListStyles;

  const pos = (getPos as any)();

  const [isCollapsibleListSelected, setIsCollapsibleListSelected] = useState(
    isNodeSelected(node.attrs.id)
  );

  const direction = componentData?.direction || languageDir;

  useEffect(() => {
    editor.on('selectionUpdate', handleSelectionUpdate);
  }, []);

  useEffect(() => {
    if (!isCollapsibleListSelected) {
      pluginsEvents?.publishPluginLayoutShift({ nodeId: node.attrs.id });
    }
  }, [isCollapsibleListSelected]);

  const handleSelectionUpdate = () => {
    setIsCollapsibleListSelected(isNodeSelected(node.attrs.id));
  };

  const addNewPair = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.commands.addCollapsibleListItem(pos + node.nodeSize);
  };

  return (
    <div
      className={classNames(styles[direction.toLowerCase()], styles.listDimensions)}
      data-hook="collapsibleListComponent"
    >
      <NodeViewContent className={classNames(styles[direction.toLowerCase()], styles.item)} />

      {isCollapsibleListSelected && (
        <NewPairButton
          onClick={addNewPair}
          label={t('CollapsibleList_ShownText_Add_Placeholder')}
        />
      )}
    </div>
  );
};

export const CollapsibleListItem: React.FC<PluginProps> = ({ editor, node, NodeViewContent }) => {
  const styles = collapsibleListItemStyles;
  const { t, languageDir } = useContext(RicosContext);
  const {
    adapter: { tiptapEditor },
  } = useContext(EditorContext);
  const $pos = editor.state.selection.$to;
  const inCollapsibleList = isInCollapsibleList($pos, node.attrs.id);
  const isExpanded = !!node.attrs.isExpanded;

  const toggleCollapsibleItem = () => {
    tiptapEditor.chain().focus().toggleCollapsibleItem(node.attrs.id);
  };

  const [mouseDown, setMouseDown] = useState<boolean>(false);

  const dir =
    findParentNodeClosestToPos($pos, node => node.type.name === TIPTAP_COLLAPSIBLE_LIST_TYPE)?.node
      ?.attrs?.direction || languageDir.toUpperCase();

  return (
    <div
      className={styles.itemContainer}
      data-hook={`${COLLAPSIBLE_LIST_ITEM} ${isExpanded ? 'expanded' : 'collapsed'}`}
    >
      {inCollapsibleList && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={classNames(styles.dndIcon, styles[dir], mouseDown && styles.mouseDown)}
          draggable="true"
          contentEditable="false"
          data-drag-handle
          onDragEnd={() => setMouseDown(false)}
          onMouseUp={() => setMouseDown(false)}
          onMouseDown={() => setMouseDown(true)}
        >
          <DndIcon />
        </div>
      )}
      <div className={styles.expandIcon}>
        <ExpandCollapseButton
          isExpanded={isExpanded}
          onClick={toggleCollapsibleItem}
          ariaLabel={
            isExpanded
              ? t('CollapsibleListPlugin_ExpandButton_AriaLabel')
              : t('CollapsibleListPlugin_CollapseButton_AriaLabel')
          }
        />
      </div>
      <NodeViewContent className={collapsibleListItemStyles.innerEditor} />
    </div>
  );
};

export const CollapsibleListItemTitle: React.FC<PluginProps> = ({ NodeViewContent }) => {
  return (
    <div
      className={classNames(
        collapsibleListItemStyles.container,
        collapsibleListItemStyles.titleContainer
      )}
    >
      <NodeViewContent
        contenteditable="true"
        className={collapsibleListItemStyles.innerEditor}
        data-hook={COLLAPSIBLE_LIST_ITEM_TITLE}
      />
    </div>
  );
};

export const CollapsibleListItemBody: React.FC<PluginProps> = ({
  NodeViewContent,
  editor,
  getPos,
  node,
}) => {
  const $pos = editor.view.state.doc.resolve((getPos as any)());
  const { languageDir } = useContext(RicosContext);

  const editorQuery = useContext(EditorQueryContext);

  const isShown = findParentNodeClosestToPos(
    $pos,
    node => node.type.name === TIPTAP_COLLAPSIBLE_ITEM_TYPE
  )?.node?.attrs?.isExpanded;

  const isFloatingMenuVisible = React.useCallback(() => {
    const selectionContainsTitle = (() => {
      const nodes = editorQuery
        .selection()
        .doc?.children?.[0]?.descendants(
          node => node.type() === TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE
        );

      return nodes.length > 0;
    })();

    const isCurrentNodeChildIsSingleTextBlock = (() =>
      editorQuery.query.isChildSingleTextBlock(node.attrs.id))();

    return !selectionContainsTitle && isCurrentNodeChildIsSingleTextBlock;
  }, [node]);

  const dir =
    findParentNodeClosestToPos(
      $pos,
      node => node.type.name === TIPTAP_COLLAPSIBLE_LIST_TYPE
    )?.node?.attrs?.direction?.toLowerCase() || languageDir;

  return isShown ? (
    <>
      <Suspense fallback={<div />}>
        <div
          className={collapsibleListItemStyles.floatingAddPluginMenuWrapper}
          onClick={preventFocusJumpToFirstNode}
          onMouseDown={preventFocusJumpToFirstNode}
          onKeyDown={preventFocusJumpToFirstNode}
        >
          <FloatingAddPluginMenu
            dir={dir}
            supportedButtonIds={supportedPluginTypes}
            isVisible={isFloatingMenuVisible}
            nodeId={node.attrs.id}
          />
        </div>
      </Suspense>

      <div className={collapsibleListItemStyles.container}>
        <NodeViewContent
          contenteditable="true"
          className={collapsibleListItemStyles.innerEditor}
          data-hook={COLLAPSIBLE_LIST_ITEM_BODY}
        />
      </div>
    </>
  ) : null;
};
